import type { PackOpenerOptions } from 'pack-opener-js';
import type { SlotOptions } from './slot';

/** Which opening mechanic a template runs. `slot` is the iframe's own
 *  slot-lane animation; the others are pack-opener-js variants. */
export type AnimationVariant = 'slot' | 'slice' | 'burst' | 'carousel';

/** The mechanics rendered by pack-opener-js. */
export type OpenerVariant = Exclude<AnimationVariant, 'slot'>;

export const ANIMATION_VARIANTS: readonly AnimationVariant[] = ['slot', 'slice', 'burst', 'carousel'];
export const OPENER_VARIANTS: readonly OpenerVariant[] = ['slice', 'burst', 'carousel'];

/**
 * The operator's delta over the library preset: only the knobs they touched,
 * in the shape pack-opener-js reads (`motion.reveal.spinMs` → `{ motion: {
 * reveal: { spinMs } } }`). `variant`, `preset` and `assets` are never in
 * here — the first two live beside it, the artwork belongs to the runtime.
 * The slot lane is the iframe's own, so its knobs sit apart under `slot`.
 */
export type AnimationOptions = Omit<PackOpenerOptions, 'variant' | 'preset' | 'assets'> & {
  slot?: SlotOptions;
};

/** Sound for the library mechanics — `design_system.animation.sound`. */
export interface SoundConfig {
  /** Off: the ceremony is silent for every player of this template. */
  enabled: boolean;
  /** Master level, 0..1. */
  volume: number;
}

/** What plays until the operator says otherwise. */
export const DEFAULT_SOUND: SoundConfig = { enabled: true, volume: 0.8 };

/**
 * The host's own controls around a library scene —
 * `design_system.animation.controls`. Not library options: the scene never
 * sees them.
 */
export interface ControlsConfig {
  /** The Rip button under the pack. Off: the pack opens by gesture alone
   *  (slice: the cut, burst: the hold) and sits centred on the screen. The
   *  carousel keeps its button either way — it is how the ring is shuffled. */
  ripButton: boolean;
  /** The pull's rarity as a giant outline word behind the card once it has
   *  landed — the slot lane's winner backdrop. Absent: off, so a mechanic
   *  looks as it always did until the operator turns it on. */
  rarityBackdrop?: boolean;
}

/** What every operator has until they turn something off. */
export const DEFAULT_CONTROLS: ControlsConfig = { ripButton: true };

/** The mechanics whose Rip button the operator may hide. */
export const RIP_BUTTON_OPTIONAL_VARIANTS: readonly AnimationVariant[] = ['slice', 'burst'];

/** The mechanics that may show the rarity word behind the landed card. */
export const RARITY_BACKDROP_VARIANTS: readonly AnimationVariant[] = ['slice', 'burst'];

/** `design_system.animation` — what the console saves and the iframe reads. */
export interface AnimationConfig {
  variant: AnimationVariant;
  /** Library preset name; only meaningful for the library mechanics. */
  preset?: string;
  /** Only present when something was changed; the slot lane keeps only `slot`. */
  options?: AnimationOptions;
  /** Only for a library mechanic; absent means `DEFAULT_SOUND`. */
  sound?: SoundConfig;
  /** Only for a library mechanic; absent means `DEFAULT_CONTROLS`. */
  controls?: ControlsConfig;
}

/** What every operator has until they save something else. */
export const DEFAULT_ANIMATION: AnimationConfig = { variant: 'slot' };

export function isOpenerVariant(variant: AnimationVariant): variant is OpenerVariant {
  return variant !== 'slot';
}

/** The mechanics whose ceremony recites the card's facts before the flip. */
export const FACT_VARIANTS: readonly AnimationVariant[] = ['carousel'];

/**
 * Whether this mechanic says anything about the card before showing it. The
 * host reads it to decide whether the reveal payload is worth asking for in
 * full: the facts cost an extra query per request (`include_card_details`),
 * and a mechanic that tells none would pay it for nothing.
 */
export function variantTellsFacts(variant: AnimationVariant): boolean {
  return FACT_VARIANTS.includes(variant);
}

/** Whether the Rip button shows for this config — hidden only where the
 *  operator turned it off and the mechanic opens without it. */
export function showsRipButton(config: AnimationConfig): boolean {
  if (!RIP_BUTTON_OPTIONAL_VARIANTS.includes(config.variant)) return true;
  return (config.controls ?? DEFAULT_CONTROLS).ripButton;
}

/** Whether the rarity word goes behind the landed card — only where the
 *  operator turned it on, for a mechanic that has it. */
export function showsRarityBackdrop(config: AnimationConfig): boolean {
  if (!RARITY_BACKDROP_VARIANTS.includes(config.variant)) return false;
  return config.controls?.rarityBackdrop === true;
}
